import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, TextField, Grid, Divider,
  Avatar, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, Switch, FormControlLabel, Alert, LinearProgress,
  Tooltip, useMediaQuery, alpha, Tabs, Tab, InputAdornment,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Save as SaveIcon,
  Upload as UploadIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Backup as BackupIcon,
  DarkMode as DarkModeIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

function Configuracoes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [openUsuario, setOpenUsuario] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, nome: '' });
  const [logoPreview, setLogoPreview] = useState(null);

  // Dados da empresa
  const [empresa, setEmpresa] = useState({
    nome: 'Jobson INFO',
    cnpj: '',
    endereco: '',
    telefone: '',
    email: '',
    logo: null,
    logo_url: null,
    website: '',
    whatsapp: ''
  });

  // Lista de usuários (do banco)
  const [usuarios, setUsuarios] = useState([]);

  // Formulário de usuário
  const [usuarioForm, setUsuarioForm] = useState({
    nome: '',
    email: '',
    senha: '',
    tipo: 'tecnico',
    ativo: true
  });

  // Preferências
  const [preferencias, setPreferencias] = useState({
    notificacoes_email: true,
    backup_automatico: true,
    modo_escuro: false,
    notificar_os_atrasadas: true
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      // Carregar dados da empresa
      const empresaRes = await api.get('/config/empresa');
      const data = empresaRes.data;
      setEmpresa(data);
      
      if (data.logo_url) {
        setLogoPreview(data.logo_url);
      } else if (data.logo) {
        setLogoPreview(`/uploads/${data.logo}`);
      }

      // Carregar usuários do banco
      const usuariosRes = await api.get('/usuarios');
      setUsuarios(usuariosRes.data);

      // Carregar preferências
      const prefData = localStorage.getItem('@Jobson:preferencias');
      if (prefData) {
        setPreferencias(JSON.parse(prefData));
      }
    } catch (error) {
      console.error('❌ Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarEmpresa = async () => {
    setSaving(true);
    try {
      const { logo, logo_url, ...dadosEmpresa } = empresa;
      await api.put('/config/empresa', dadosEmpresa);
      toast.success('Dados da empresa salvos com sucesso!');
      await carregarDados();
    } catch (error) {
      console.error('❌ Erro ao salvar dados:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar dados da empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Formato não suportado. Use JPEG ou PNG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/config/empresa/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setLogoPreview(response.data.logo_url);
      setEmpresa({ ...empresa, logo: response.data.logo, logo_url: response.data.logo_url });
      toast.success('Logo atualizada com sucesso!');
      await carregarDados();
    } catch (error) {
      console.error('❌ Erro ao fazer upload da logo:', error);
      toast.error(error.response?.data?.error || 'Erro ao fazer upload da logo');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleOpenUsuario = (usuario = null) => {
    if (usuario) {
      setUsuarioEditando(usuario);
      setUsuarioForm({
        nome: usuario.nome,
        email: usuario.email,
        senha: '',
        tipo: usuario.tipo,
        ativo: usuario.ativo
      });
    } else {
      setUsuarioEditando(null);
      setUsuarioForm({
        nome: '',
        email: '',
        senha: '',
        tipo: 'tecnico',
        ativo: true
      });
    }
    setOpenUsuario(true);
  };

  const handleSalvarUsuario = async () => {
    setSaving(true);
    try {
      if (usuarioEditando) {
        await api.put(`/usuarios/${usuarioEditando.id}`, usuarioForm);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await api.post('/usuarios', usuarioForm);
        toast.success('Usuário criado com sucesso!');
      }
      setOpenUsuario(false);
      setUsuarioEditando(null);
      setUsuarioForm({ nome: '', email: '', senha: '', tipo: 'tecnico', ativo: true });
      await carregarDados();
    } catch (error) {
      console.error('❌ Erro ao salvar usuário:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUsuario = (id, nome) => {
    setDeleteDialog({ open: true, id, nome });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/usuarios/${deleteDialog.id}`);
      toast.success('Usuário excluído com sucesso!');
      setDeleteDialog({ open: false, id: null, nome: '' });
      await carregarDados();
    } catch (error) {
      console.error('❌ Erro ao excluir usuário:', error);
      toast.error(error.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const handleSalvarPreferencias = () => {
    localStorage.setItem('@Jobson:preferencias', JSON.stringify(preferencias));
    toast.success('Preferências salvas com sucesso!');
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      admin: 'Administrador',
      tecnico: 'Técnico',
      financeiro: 'Financeiro',
      suporte: 'Suporte',
      super_admin: 'Super Admin'
    };
    return labels[tipo] || tipo;
  };

  const getTipoColor = (tipo) => {
    const colors = {
      admin: 'primary',
      tecnico: 'info',
      financeiro: 'success',
      suporte: 'warning',
      super_admin: 'secondary'
    };
    return colors[tipo] || 'default';
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'admin':
      case 'super_admin':
        return <AdminIcon sx={{ color: '#0A2647' }} />;
      case 'financeiro':
        return <LockIcon sx={{ color: '#2ecc71' }} />;
      default:
        return <PersonIcon sx={{ color: '#3498db' }} />;
    }
  };

  if (loading) {
    return (
      <Layout title="Configurações">
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: alpha('#0A2647', 0.1),
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#0A2647',
              borderRadius: 4
            }
          }} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Configurações">
      <PageHeader
        title="Configurações"
        subtitle="Gerencie as configurações do sistema"
        icon={<SettingsIcon sx={{ color: '#39FF14' }} />}
      />

      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: { xs: 1, sm: 2 },
          overflowX: 'auto'
        }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                minWidth: isMobile ? 'auto' : 120,
                px: { xs: 2, sm: 3 }
              },
              '& .Mui-selected': {
                color: '#0A2647'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#0A2647'
              }
            }}
          >
            <Tab icon={<BusinessIcon />} label="Empresa" />
            <Tab icon={<PersonIcon />} label="Usuários" />
            <Tab icon={<SettingsIcon />} label="Preferências" />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ color: '#0A2647', fontWeight: 600, mb: 3 }}>
                    Dados da Empresa
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                        <Avatar
                          src={logoPreview}
                          sx={{
                            width: { xs: 80, sm: 100 },
                            height: { xs: 80, sm: 100 },
                            border: '2px solid #0A2647',
                            bgcolor: '#f0f0f0',
                            fontSize: { xs: 32, sm: 40 }
                          }}
                        >
                          {!logoPreview && empresa.nome?.charAt(0)?.toUpperCase() || 'E'}
                        </Avatar>
                        <Box>
                          <input
                            accept="image/jpeg,image/png,image/jpg"
                            style={{ display: 'none' }}
                            id="logo-upload"
                            type="file"
                            onChange={handleLogoUpload}
                          />
                          <label htmlFor="logo-upload">
                            <Button
                              variant="outlined"
                              component="span"
                              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                              disabled={uploading}
                              sx={{
                                borderRadius: 2,
                                borderColor: '#0A2647',
                                color: '#0A2647',
                                '&:hover': {
                                  borderColor: '#0A2647',
                                  bgcolor: alpha('#0A2647', 0.04)
                                }
                              }}
                            >
                              {uploading ? 'Enviando...' : 'Upload Logo'}
                            </Button>
                          </label>
                          <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                            Tamanho: 150x150px (PNG/JPG) • Máx: 5MB
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Nome da Empresa"
                        value={empresa.nome}
                        onChange={(e) => setEmpresa({...empresa, nome: e.target.value})}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon sx={{ color: 'rgba(10,38,71,0.5)' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="CNPJ"
                        value={empresa.cnpj}
                        onChange={(e) => setEmpresa({...empresa, cnpj: e.target.value})}
                        placeholder="00.000.000/0000-00"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Telefone"
                        value={empresa.telefone}
                        onChange={(e) => setEmpresa({...empresa, telefone: e.target.value})}
                        placeholder="(83) 9999-1234"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon sx={{ color: 'rgba(10,38,71,0.5)' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="WhatsApp"
                        value={empresa.whatsapp || ''}
                        onChange={(e) => setEmpresa({...empresa, whatsapp: e.target.value})}
                        placeholder="(83) 99999-9999"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon sx={{ color: 'rgba(10,38,71,0.5)' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        type="email"
                        value={empresa.email}
                        onChange={(e) => setEmpresa({...empresa, email: e.target.value})}
                        placeholder="contato@jobson.com"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: 'rgba(10,38,71,0.5)' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Website"
                        value={empresa.website || ''}
                        onChange={(e) => setEmpresa({...empresa, website: e.target.value})}
                        placeholder="www.jobson.com"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Endereço"
                        value={empresa.endereco}
                        onChange={(e) => setEmpresa({...empresa, endereco: e.target.value})}
                        placeholder="Av Jaguaribe, 123 - João Pessoa PB"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon sx={{ color: 'rgba(10,38,71,0.5)' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                          onClick={handleSalvarEmpresa}
                          disabled={saving}
                          sx={{
                            borderRadius: 2,
                            px: 4,
                            bgcolor: '#0A2647',
                            color: '#ffffff',
                            '&:hover': {
                              bgcolor: '#1a3b5c',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px rgba(10,38,71,0.3)'
                            },
                            '&:disabled': {
                              bgcolor: alpha('#0A2647', 0.6),
                              color: '#ffffff'
                            }
                          }}
                        >
                          {saving ? 'Salvando...' : 'Salvar Dados'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <Typography variant="h6" sx={{ color: '#0A2647', fontWeight: 600 }}>
                      Gerenciar Usuários
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenUsuario()}
                      sx={{
                        borderRadius: 2,
                        bgcolor: '#0A2647',
                        color: '#ffffff',
                        '&:hover': {
                          bgcolor: '#1a3b5c',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(10,38,71,0.3)'
                        }
                      }}
                    >
                      {isMobile ? 'Novo' : 'Novo Usuário'}
                    </Button>
                  </Box>

                  {isMobile ? (
                    <Box>
                      {usuarios.map((usuario) => (
                        <Card
                          key={usuario.id}
                          sx={{
                            mb: 1.5,
                            borderRadius: 2,
                            border: '1px solid rgba(0,0,0,0.06)'
                          }}
                        >
                          <Box sx={{ p: 1.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{
                                  bgcolor: alpha('#0A2647', 0.1),
                                  color: '#0A2647',
                                  width: 40,
                                  height: 40
                                }}>
                                  {getTipoIcon(usuario.tipo)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                                    {usuario.nome}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {usuario.email}
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip
                                label={usuario.ativo ? 'Ativo' : 'Inativo'}
                                size="small"
                                color={usuario.ativo ? 'success' : 'default'}
                                sx={{ borderRadius: 2, fontWeight: 500 }}
                              />
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                              <Chip
                                label={getTipoLabel(usuario.tipo)}
                                size="small"
                                color={getTipoColor(usuario.tipo)}
                                sx={{ borderRadius: 2, fontWeight: 500 }}
                              />
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenUsuario(usuario)}
                                  sx={{ color: '#0A2647' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteUsuario(usuario.id, usuario.nome)}
                                  sx={{ color: '#d32f2f' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha('#0A2647', 0.04) }}>
                            <TableCell><strong>Usuário</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Tipo</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell align="center"><strong>Ações</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {usuarios.map((usuario) => (
                            <TableRow key={usuario.id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{
                                    bgcolor: alpha('#0A2647', 0.1),
                                    color: '#0A2647',
                                    width: 32,
                                    height: 32
                                  }}>
                                    {usuario.nome?.charAt(0)?.toUpperCase() || 'U'}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {usuario.nome}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{usuario.email}</TableCell>
                              <TableCell>
                                <Chip
                                  label={getTipoLabel(usuario.tipo)}
                                  size="small"
                                  color={getTipoColor(usuario.tipo)}
                                  sx={{ borderRadius: 2, fontWeight: 500 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={usuario.ativo ? 'Ativo' : 'Inativo'}
                                  color={usuario.ativo ? 'success' : 'default'}
                                  size="small"
                                  sx={{ borderRadius: 2, fontWeight: 500 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Editar">
                                  <IconButton
                                    onClick={() => handleOpenUsuario(usuario)}
                                    sx={{ color: '#0A2647' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir">
                                  <IconButton
                                    onClick={() => handleDeleteUsuario(usuario.id, usuario.nome)}
                                    sx={{ color: '#d32f2f' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" sx={{ color: '#0A2647', fontWeight: 600, mb: 3 }}>
                    Preferências do Sistema
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={preferencias.notificacoes_email}
                              onChange={(e) => setPreferencias({...preferencias, notificacoes_email: e.target.checked})}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#0A2647',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#0A2647',
                                },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#0A2647' }}>
                                <NotificationsIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                                Notificações por Email
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Receba notificações sobre OS, pagamentos e atualizações
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start' }}
                        />
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={preferencias.backup_automatico}
                              onChange={(e) => setPreferencias({...preferencias, backup_automatico: e.target.checked})}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#0A2647',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#0A2647',
                                },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#0A2647' }}>
                                <BackupIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                                Backup Automático
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Realizar backup diário dos dados do sistema
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start' }}
                        />
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={preferencias.notificar_os_atrasadas}
                              onChange={(e) => setPreferencias({...preferencias, notificar_os_atrasadas: e.target.checked})}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#0A2647',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#0A2647',
                                },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#0A2647' }}>
                                <NotificationsIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                                Alertas de OS Atrasadas
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Receber notificações quando uma OS ultrapassar o prazo
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start' }}
                        />
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={preferencias.modo_escuro}
                              onChange={(e) => setPreferencias({...preferencias, modo_escuro: e.target.checked})}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#0A2647',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#0A2647',
                                },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#0A2647' }}>
                                <DarkModeIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                                Modo Escuro
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Alternar entre tema claro e escuro
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start' }}
                        />
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSalvarPreferencias}
                          sx={{
                            borderRadius: 2,
                            px: 4,
                            bgcolor: '#0A2647',
                            color: '#ffffff',
                            '&:hover': {
                              bgcolor: '#1a3b5c',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px rgba(10,38,71,0.3)'
                            }
                          }}
                        >
                          Salvar Preferências
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Card>

      {/* Dialog de Usuário */}
      <Dialog
        open={openUsuario}
        onClose={() => setOpenUsuario(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 40px 80px rgba(0,0,0,0.2)'
          }
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            background: 'rgba(57, 255, 20, 0.05)',
            borderRadius: '50%'
          }} />
          <Box display="flex" alignItems="center" gap={2} position="relative" zIndex={1}>
            <Avatar sx={{
              bgcolor: 'rgba(57, 255, 20, 0.15)',
              color: '#39FF14',
              width: 48,
              height: 48
            }}>
              {usuarioEditando ? <EditIcon /> : <PersonAddIcon />}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                {usuarioEditando ? 'Editar Usuário' : 'Novo Usuário'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {usuarioEditando ? 'Atualize os dados do usuário' : 'Preencha os dados para cadastrar'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="medium"
                label="Nome *"
                value={usuarioForm.nome}
                onChange={(e) => setUsuarioForm({...usuarioForm, nome: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="medium"
                label="Email *"
                type="email"
                value={usuarioForm.email}
                onChange={(e) => setUsuarioForm({...usuarioForm, email: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="medium"
                label="Senha"
                type="password"
                value={usuarioForm.senha}
                onChange={(e) => setUsuarioForm({...usuarioForm, senha: e.target.value})}
                placeholder={usuarioEditando ? 'Deixe em branco para manter' : 'Mínimo 6 caracteres'}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="medium">
                <InputLabel>Tipo de Usuário</InputLabel>
                <Select
                  value={usuarioForm.tipo}
                  label="Tipo de Usuário"
                  onChange={(e) => setUsuarioForm({...usuarioForm, tipo: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="admin">🔐 Administrador</MenuItem>
                  <MenuItem value="tecnico">🔧 Técnico</MenuItem>
                  <MenuItem value="financeiro">💰 Financeiro</MenuItem>
                  <MenuItem value="suporte">🆘 Suporte</MenuItem>
                  <MenuItem value="super_admin">⭐ Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={usuarioForm.ativo}
                    onChange={(e) => setUsuarioForm({...usuarioForm, ativo: e.target.checked})}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#0A2647',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#0A2647',
                      },
                    }}
                  />
                }
                label="Usuário Ativo"
                sx={{ color: '#0A2647' }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={() => setOpenUsuario(false)}
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              borderRadius: 2,
              color: '#0A2647',
              borderColor: '#0A2647',
              '&:hover': {
                borderColor: '#0A2647',
                bgcolor: alpha('#0A2647', 0.04)
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSalvarUsuario}
            variant="contained"
            disabled={saving}
            fullWidth={isMobile}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              borderRadius: 2,
              px: isMobile ? 0 : 4,
              bgcolor: '#0A2647',
              color: '#ffffff',
              '&:hover': {
                bgcolor: '#1a3b5c',
                color: '#ffffff',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(10,38,71,0.3)'
              },
              '&:disabled': {
                bgcolor: alpha('#0A2647', 0.6),
                color: '#ffffff'
              }
            }}
          >
            {saving ? 'Salvando...' : usuarioEditando ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, nome: '' })}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 2,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxWidth: isMobile ? '95%' : 'auto'
          }
        }}
      >
        <Box sx={{ textAlign: 'center', p: isMobile ? 1 : 2 }}>
          <Avatar sx={{
            bgcolor: 'rgba(211, 47, 47, 0.1)',
            color: '#d32f2f',
            width: isMobile ? 50 : 60,
            height: isMobile ? 50 : 60,
            mx: 'auto',
            mb: 2
          }}>
            <DeleteIcon sx={{ fontSize: isMobile ? 24 : 30 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Confirmar exclusão
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Deseja realmente excluir o usuário <strong>{deleteDialog.nome}</strong>?
            <br />
            Esta ação não pode ser desfeita.
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <Button
              onClick={() => setDeleteDialog({ open: false, id: null, nome: '' })}
              variant="outlined"
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                px: isMobile ? 0 : 4,
                color: '#0A2647',
                borderColor: '#0A2647'
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                px: isMobile ? 0 : 4,
                color: '#ffffff',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(211,47,47,0.3)'
                }
              }}
            >
              Excluir
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Layout>
  );
}

export default Configuracoes;
